import { getAuthToken, handleTokenExpiration } from '../utils/tokenUtils'
import useLoadingStore from '../stores/loadingStore'
import api from '../utils/api'

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001'

// Helper function to handle the core generation creation logic
async function handleGenerationCreation(generationData, token) {
  const response = await fetch(`${API_BASE_URL}/generations`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(generationData)
  })

  if (response.status === 400) {
    const error = await response.json()
    if (error.message === 'Invalid Token') {
      handleTokenExpiration()
    }
    throw new Error(error.message || 'Failed to create generation')
  }

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || 'Failed to create generation')
  }

  const result = await response.json()
  
  return {
    ...result,
    groupId: result.groupId,
    originalGenerationId: result._id,
    versions: [{
      id: result._id,
      versionNumber: 0,
      isOriginalVersion: true,
      status: 'processing',
      createdAt: result.startedAt || new Date().toISOString()
    }],
    totalVersions: 1
  }
}

// Add this new utility function
const loadImagesInParallel = async (imageIds, token) => {
  if (!Array.isArray(imageIds) || imageIds.length === 0) {
    return new Map()
  }

  // Create a map to store object URLs
  const objectUrls = new Map()

  try {
    // Create an array of fetch promises
    const fetchPromises = imageIds.map(async (id) => {
      try {
        const response = await fetch(`${API_BASE_URL}/generations/image/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (!response.ok) {
          throw new Error(`Failed to load image ${id}`)
        }

        const blob = await response.blob()
        const objectUrl = URL.createObjectURL(blob)
        objectUrls.set(id, objectUrl)
        
        return { id, success: true }
      } catch (error) {
        console.error(`Failed to load image ${id}:`, error)
        return { id, success: false, error }
      }
    })

    // Wait for all fetches to complete
    await Promise.all(fetchPromises)
    
    return objectUrls
  } catch (error) {
    console.error('Parallel image loading failed:', error)
    // Clean up any successfully created object URLs
    objectUrls.forEach(url => URL.revokeObjectURL(url))
    throw error
  }
}

// Add request tracking
let lastStatusCheck = 0;
const MIN_STATUS_CHECK_INTERVAL = 2000; // 2 seconds minimum between checks

export const generationsApi = {
  create: async (generationData) => {
    const setIsGenerating = useLoadingStore.getState().setIsGenerating
    const setGenerationProgress = useLoadingStore.getState().setGenerationProgress
    
    // Validate required fields and model
    if (!generationData.prompt || !generationData.orientation || !generationData.style) {
      throw new Error('Missing required fields for generation')
    }

    // Strict model validation - no fallbacks
    if (!generationData.model) {
      throw new Error('Model must be specified')
    }
    if (generationData.model !== 'flux1d' && generationData.model !== 'sdxl21') {
      throw new Error(`Model ${generationData.model} is not supported. Only flux1d and sdxl21 are available.`)
    }

    setIsGenerating(true)
    setGenerationProgress(0)
    
    let progressInterval

    try {
      const token = getAuthToken()
      
      // Start progress simulation
      progressInterval = setInterval(() => {
        setGenerationProgress(prev => Math.min(prev + 10, 90))
      }, 1000)
      
      // Force model to flux1d
      const result = await handleGenerationCreation({
        ...generationData
      }, token)
      
      // Complete the progress
      clearInterval(progressInterval)
      setGenerationProgress(100)
      
      // Reset loading state after a brief delay
      setTimeout(() => {
        setIsGenerating(false)
        setGenerationProgress(0)
      }, 500)
      
      return result
    } catch (error) {
      // Clean up on error
      if (progressInterval) {
        clearInterval(progressInterval)
      }
      setIsGenerating(false)
      setGenerationProgress(0)
      throw error
    }
  },

  getAll: async () => {
    const token = getAuthToken()
    if (process.env.NODE_ENV !== 'production') {
      console.log('Getting generations with token:', token ? 'exists' : 'not found')
    }
    
    const response = await fetch(`${API_BASE_URL}/generations`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (response.status === 400) {
      const error = await response.json()
      if (error.message === 'Invalid Token') {
        handleTokenExpiration()
      }
      throw new Error(error.message || 'Failed to fetch generations')
    }

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to fetch generations')
    }

    // Response is now already grouped from the backend
    const groups = await response.json()
    
    // Ensure all required fields exist and are properly formatted
    return groups.map(group => ({
      ...group,
      versions: group.versions.map(version => ({
        ...version,
        id: version._id || version.id,
        versionNumber: version.versionNumber || 0,
        isOriginalVersion: version.isOriginalVersion || false
      }))
    }))
  },

  checkStatus: async (generationIds) => {
    // Implement request debouncing
    const now = Date.now();
    const timeSinceLastCheck = now - lastStatusCheck;
    
    if (timeSinceLastCheck < MIN_STATUS_CHECK_INTERVAL) {
      console.debug('Status check too frequent, skipping');
      return [];
    }
    
    // Filter out invalid IDs
    const validIds = generationIds.filter(id => 
      typeof id === 'string' && id.length === 24
    );

    if (validIds.length === 0) {
      console.warn('No valid generation IDs to check');
      return [];
    }

    lastStatusCheck = now;
    const token = getAuthToken();
    
    try {
      const response = await fetch(`${API_BASE_URL}/generations/status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ids: validIds })
      });

      if (response.status === 400) {
        const error = await response.json();
        if (error.message === 'Invalid Token') {
          handleTokenExpiration();
        }
        console.warn('Status check failed:', error.message);
        return [];
      }

      if (!response.ok) {
        const error = await response.json();
        console.error('Status check error:', error);
        return [];
      }

      const updatedVersions = await response.json();
      
      // Transform response to include version information
      return updatedVersions.map(version => ({
        ...version,
        id: version._id || version.id,
        versionNumber: version.versionNumber || 0,
        isOriginalVersion: version.isOriginalVersion || false,
        groupId: version.groupId
      }));
    } catch (error) {
      console.error('Status check failed:', error);
      return [];
    }
  },

  getImageUrl: async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/generations/image/${id}`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      })

      if (response.status === 400) {
        const error = await response.json()
        if (error.message === 'Invalid Token') {
          handleTokenExpiration()
        }
        throw new Error(error.message || 'Failed to fetch image URL')
      }

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Failed to fetch image URL')
      }

      return response.json()
    } catch (error) {
      console.error('Failed to get image URL:', error)
      throw error
    }
  },

  saveVersion: async (generationId, imageData, adjustments) => {
    const token = getAuthToken()
    
    const response = await fetch(`${API_BASE_URL}/generations/${generationId}/versions`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ imageData, adjustments })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to save version')
    }

    const result = await response.json()
    
    // Transform response to include version information
    return {
      ...result,
      version: {
        ...result.version,
        id: result.version.id,
        versionNumber: result.version.versionNumber,
        isOriginalVersion: false,
        groupId: result.version.groupId,
        createdAt: result.version.createdAt
      }
    }
  },

  deleteGroup: async (groupId) => {
    const token = getAuthToken()
    
    const response = await fetch(`${API_BASE_URL}/generations/group/${groupId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to delete group')
    }

    return response.json()
  },

  loadImagesInParallel,
  
  // Add a cleanup utility
  revokeObjectUrls: (objectUrls) => {
    if (objectUrls instanceof Map) {
      objectUrls.forEach(url => URL.revokeObjectURL(url))
      objectUrls.clear()
    }
  },

  createToolVersion: async (generationId, toolRequest) => {
    const token = getAuthToken()
    
    // Validate required fields
    if (!generationId || !toolRequest.toolType || !toolRequest.params) {
      throw new Error('Missing required tool request parameters')
    }

    try {
      const response = await fetch(`${API_BASE_URL}/generations/${generationId}/tool-version`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache'
        },
        body: JSON.stringify(toolRequest)
      })

      const data = await response.json()

      // Handle token expiration
      if (response.status === 401) {
        handleTokenExpiration()
        throw new Error('Session expired')
      }

      if (response.status === 400) {
        throw new Error(data.error || 'Invalid tool request parameters')
      }

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create tool version')
      }

      return {
        ...data,
        id: data._id,
        versionNumber: data.versionNumber || 0,
        status: data.status || 'processing',
        createdAt: data.createdAt || new Date().toISOString(),
        toolMetadata: {
          toolType: toolRequest.toolType,
          params: toolRequest.params
        }
      }
    } catch (error) {
      console.error('Tool version creation error:', error)
      throw error // Propagate the original error
    }
  },

  checkVersionStatus: async (versionId) => {
    const token = getAuthToken()
    
    try {
      const response = await fetch(`${API_BASE_URL}/generations/versions/${versionId}/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.status === 400) {
        const error = await response.json()
        if (error.message === 'Invalid Token') {
          handleTokenExpiration()
        }
        throw new Error(error.message || 'Failed to check version status')
      }

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Failed to check version status')
      }

      return await response.json()
    } catch (error) {
      console.error('Version status check failed:', error)
      throw error
    }
  },

  // Add method to get all versions for a generation
  getVersions: async (generationId) => {
    return api.get(`/generations/${generationId}/versions`)
  },

  generateImage: async (generationData) => {
    try {
      // Always force flux1d model
      const response = await api.post('/generations', {
        ...generationData
      })
      return response.data
    } catch (error) {
      throw error
    }
  }
}

export default generationsApi